import { useState, type MouseEvent, type ReactNode } from "react";
import { AlertTriangle, FileText, Loader2, Monitor, Rocket, Save, Smartphone } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Button } from "@/shared/ui/button";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";
import { Skeleton } from "@/shared/ui/skeleton";
import { cn } from "@/shared/lib/utils";

import { placeholderFacts } from "../model/content";
import { isHostSite } from "../model/hostNodes";
import { BlockForm } from "./components/BlockForm";
import { BrandForm } from "./components/editor/BrandForm";
import { PageBlockList } from "./components/editor/PageBlockList";
import { SitePreview, type PreviewDevice } from "./components/SitePreview";
import { useCanManageSite } from "./hooks/useCanManageSite";
import { useDraftEditor } from "./hooks/useDraftEditor";
import HostEditPage from "./HostEditPage";
import { SiteShell } from "./SiteShell";

/**
 * 屏 4「内容编辑」:左 400px = 页面列表 + 区块列表(上下箭头改序,不拖拽)+ 站点基础 + 当前区块表单;
 * 右 = 简版实时预览;页头 = 电脑 / 手机切换、保存草稿、发布 v{n}。
 * 编辑器状态(函数式写、保存竞态、发布、AI 重写、离开守卫、防串台)在 `useDraftEditor`。
 *
 * ★ 事实字段还是占位 `[待补:…]` 的(产品 / 案例 / 资质这页不给表单),表单顶上提示回资料采集补齐,
 *   「发布」禁用并在 title 里说清(发布前校验要求零占位,附录 A.2)。
 * ★ 有未保存改动时,「← 返回」和「回到资料采集补齐」先问(保存并离开 / 不保存再确认一次 / 取消);
 *   关标签 / 刷新由 `beforeunload` 挡。
 * ★ 还没出过初稿(内容 404):画「还没有生成初稿」+ 回采集页的链接,不当报错。
 * ★ host 模式(A.57:`publisherMode == host`,或草稿带 `transplant`)整页交给 `HostEditPage`:节点树 + 节点编辑 + 图片区,
 *   不渲染 `SitePreview`。
 * ★ STAFF 看不到「发布」(A.59,发布只给 TENANT_MANAGER+)。
 */
export default function EditPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const ed = useDraftEditor(id);
  const canManage = useCanManageSite();
  const { site, template, content, dirty, saving, publishing } = ed;
  const [pageSlug, setPageSlug] = useState<string | null>(null);
  const [blockId, setBlockId] = useState<string | null>(null);
  const [device, setDevice] = useState<PreviewDevice>("desktop");
  const collectPath = `/ai-website/sites/${encodeURIComponent(id)}/collect`;

  /** 站内链接离开前过一道闸:没改动照常跳;有改动先问,问完再跳。 */
  const guardLink = (to: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    if (!dirty) return;
    e.preventDefault();
    void ed.confirmLeave().then((ok) => {
      if (ok) navigate(to);
    });
  };

  if (ed.error) {
    return <QueryErrorBanner isError message={ed.error} onRetry={() => void ed.load()} />;
  }
  if (ed.noDraft && site) {
    return (
      <SiteShell site={site} pageTitle="内容编辑" subtitle="还没有生成初稿">
        <div
          role="status"
          className={cn(
            "flex flex-col items-center gap-3 border border-dashed border-border bg-card px-6 py-12 text-center",
            CARD_WALL_RADIUS
          )}
        >
          <FileText className="h-8 w-8 text-muted-foreground" aria-hidden />
          <h2 className="text-sm font-semibold text-foreground">还没有生成初稿</h2>
          <p className="text-xs text-muted-foreground">
            先在资料采集页补齐资料、生成初稿,再回来编辑。
          </p>
          <Button asChild size="sm">
            <Link to={collectPath}>去资料采集生成初稿</Link>
          </Button>
        </div>
      </SiteShell>
    );
  }
  if (!site || !template || !content) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-2/3" />
        <Skeleton className="h-6 w-1/2" />
        <div className="grid gap-6 xl:grid-cols-[400px_minmax(0,1fr)]">
          <Skeleton className="h-96 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (isHostSite(site, content)) return <HostEditPage ed={ed} />;

  const nextVersion = (site.currentVersionNo ?? 0) + 1;
  const page = content.pages.find((p) => p.slug === pageSlug) ?? content.pages[0];
  const block = page?.blocks.find((b) => b.id === blockId) ?? page?.blocks[0] ?? null;
  const busy = saving || publishing;
  const pendingFacts = placeholderFacts(template, content);
  const publishBlocked =
    pendingFacts.length > 0
      ? `还有 ${pendingFacts.length} 项资料待补(${[
          ...new Set(pendingFacts.map((f) => f.label)),
        ].join("、")}),补齐后才能发布`
      : undefined;

  const selectPage = (slug: string) => {
    setPageSlug(slug);
    setBlockId(content.pages.find((p) => p.slug === slug)?.blocks[0]?.id ?? null);
  };

  return (
    <SiteShell
      site={site}
      pageTitle="内容编辑"
      beforeLeave={ed.confirmLeave}
      // 设计稿写「N 分钟前自动保存」,但本页没有自动保存(只有手动「保存草稿」),照实写已保存 / 未保存。
      subtitle={`v${nextVersion} 草稿 · 未发布 · ${dirty ? "未保存" : "已保存"}${
        site.currentVersionNo != null ? ` · 线上仍是 v${site.currentVersionNo}` : ""
      }`}
      actions={
        <>
          <div role="group" aria-label="预览设备" className="flex rounded-md border border-border">
            <DeviceButton on={device === "desktop"} onClick={() => setDevice("desktop")}>
              <Monitor className="h-3.5 w-3.5" />
              电脑
            </DeviceButton>
            <DeviceButton on={device === "mobile"} onClick={() => setDevice("mobile")}>
              <Smartphone className="h-3.5 w-3.5" />
              手机
            </DeviceButton>
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={!dirty || busy}
            onClick={() => void ed.persist()}
          >
            {saving ? <Loader2 className="animate-spin" /> : <Save />}
            保存草稿
          </Button>
          {canManage && (
            <Button
              size="sm"
              disabled={busy || publishBlocked !== undefined}
              title={publishBlocked}
              onClick={() => void ed.publish()}
            >
              {publishing ? <Loader2 className="animate-spin" /> : <Rocket />}
              发布 v{nextVersion}
            </Button>
          )}
        </>
      }
    >
      {/* items-start:预览卡按自己的内容收高,不被左栏表单拉出一大块空底。 */}
      <div className="grid gap-6 xl:grid-cols-[400px_minmax(0,1fr)] xl:items-start">
        <div className="space-y-4">
          {pendingFacts.length > 0 && (
            <p
              role="note"
              className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-foreground"
            >
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
              <span>
                有 {pendingFacts.length} 项资料待补,请
                <Link
                  to={collectPath}
                  onClick={guardLink(collectPath)}
                  className="font-medium text-primary underline-offset-2 hover:underline"
                >
                  回到资料采集补齐
                </Link>
              </span>
            </p>
          )}

          <PageBlockList
            pages={content.pages}
            page={page}
            activeBlockId={block?.id ?? null}
            busy={busy}
            onSelectPage={selectPage}
            onSelectBlock={setBlockId}
            onMove={(bid, dir) => page && ed.moveBlock(page.slug, bid, dir)}
          />

          <BrandForm template={template} content={content} editor={ed} />

          {page && block && (
            <BlockForm
              template={template}
              content={content}
              pageSlug={page.slug}
              block={block}
              editor={ed}
            />
          )}
        </div>

        <SitePreview
          content={content}
          previewUrl={site.previewUrl}
          pageSlug={page?.slug ?? ""}
          activeBlockId={block?.id ?? null}
          device={device}
        />
      </div>
    </SiteShell>
  );
}

function DeviceButton({
  on,
  onClick,
  children,
}: {
  on: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium transition-colors first:rounded-l-md last:rounded-r-md",
        on ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"
      )}
    >
      {children}
    </button>
  );
}
